import type { FC } from 'react'

import type { AssetType } from '@skytech/types'
import { AssetFallbackIcon } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { twJoin, twMerge } from 'tailwind-merge'

import injuryreportResources from '@/api/injuryReports'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import useSetup from '@/hooks/setup'

import { Translation } from '../i18n/Translation'
import { LocalDate } from '../LocalDate'
import { statusStyleByWhiteLabel } from '../util/style-constant'

import { BuyOutOffering } from './BuyOutOffering'
import { goToForm } from './helpers'
import { InjuryReportList } from './InjuryReportList'
import { injuryReportStillOpen } from './injuryStatuses'
import type { MyAssetsAsset } from './types'

export const AssetCard: FC<MyAssetsAsset & { highlighted?: boolean }> = ({
  id,
  type,
  typeId,
  articleImageUrl,
  assetDescription,
  received,
  imeiSnr,
  statusId,
  isLeased,
  leaseStart,
  leaseEnd,
  injuryReports,
  buyoutOffering,
  highlighted,
}) => {
  const queryClient = useQueryClient()
  const { whiteLabel } = useAppContext()

  const setup = useSetup()
  const repairReturnText = setup?.repair_return_text

  const createReportMutation = useMutation((id: number) => injuryreportResources.post(id), {
    onSuccess(assets: Omit<MyAssetsAsset, 'buyoutDetails'>[], assetId: number) {
      const injuryReportsForAsset = assets.find(asset => assetId === asset.id)?.injuryReports
      if (!injuryReportsForAsset) return

      const lastInjuryReport = injuryReportsForAsset.sort(
        (a, b) => b.injuryReportId - a.injuryReportId
      )?.[0]

      if (lastInjuryReport && lastInjuryReport.injuryReportCode) {
        goToForm(lastInjuryReport.injuryReportCode)
      }

      queryClient.invalidateQueries(queries.injuryReportAssets())
    },
  })

  const isMobilePhone = typeId === 2
  const isForReturn = statusId === 4
  const isBoughtOut = statusId === 7
  const hasSpecialStatus = isForReturn

  const noOpenInjuryReports = !injuryReports?.some(report =>
    injuryReportStillOpen(report.injuryReportStatusId)
  )

  const canOpenInjuryReport =
    noOpenInjuryReports && isMobilePhone && repairReturnText && statusId === 1

  // Style based on white label
  const styleByWhiteLabel = statusStyleByWhiteLabel[whiteLabel] || statusStyleByWhiteLabel.skytech

  return (
    <div
      id={`asset-${id}`}
      className={twJoin(
        'group mb-4 rounded-lg border-2 border-solid border-gray-300 bg-white shadow-sm',
        highlighted && 'animate-asset-highlight'
      )}
    >
      {/* Main Content Row */}
      <div className="small-mobile:flex-row small-mobile:gap-4 relative flex flex-col gap-1 p-4 md:gap-6">
        {/* Status indicator for return */}
        {isForReturn && (
          <>
            {/* Background overlay */}
            <div
              className={twMerge(
                'absolute inset-0 rounded-lg opacity-30 transition-opacity duration-300 group-hover:opacity-0',
                styleByWhiteLabel?.warning.background
              )}
            />

            {/* Status badge and icon */}
            <div className="absolute inset-0 flex items-center justify-center rounded-lg">
              <span
                className={twMerge(
                  'relative z-10 rounded-lg bg-white px-4 py-2 text-lg font-bold uppercase tracking-wide shadow-md transition-opacity duration-300 group-hover:opacity-0',
                  styleByWhiteLabel?.warning.text
                )}
              >
                <Translation id="Pending Return" />
              </span>
            </div>
          </>
        )}

        {/* Content with reduced opacity when status is active */}
        <div
          className={twJoin(
            'flex flex-shrink-0 items-center justify-center self-center transition-opacity duration-300',
            hasSpecialStatus && 'opacity-60 group-hover:opacity-100'
          )}
        >
          <div className="flex h-20 w-20 items-center justify-center">
            <ImageWithIconFallback
              src={articleImageUrl}
              alt="Product"
              fallbackIcon={<AssetFallbackIcon assetType={(type as AssetType) || 'OTHER'} />}
              className={twJoin('h-full w-full object-contain')}
            />
          </div>
        </div>
        {/* Content Container */}
        <div
          className={twJoin(
            'flex flex-1 flex-col transition-opacity duration-300 md:flex-row md:items-center md:gap-6',
            hasSpecialStatus && 'opacity-60 group-hover:opacity-100'
          )}
        >
          {/* Text Content */}
          <div className="flex-1">
            {/* Product Name */}
            <h3 className="small-mobile:text-left !m-0 mb-2 text-justify text-base font-medium text-gray-900">
              {assetDescription}
            </h3>

            {/* Details Row */}
            <div className="grid grid-cols-1 gap-1 text-sm text-gray-600 sm:grid-cols-2 sm:gap-4">
              <div>
                <Translation id="Mottatt:" /> <LocalDate date={received} />
              </div>
              <div>
                <span>{imeiSnr}</span>
              </div>
            </div>

            {/* Lease Details Row */}
            {isLeased && (
              <div className="grid grid-cols-1 gap-1 text-sm text-gray-600 sm:grid-cols-2 sm:gap-4">
                <div>
                  <Translation id="Lease Start:" /> <LocalDate date={leaseStart} />
                </div>
                <div>
                  <Translation id="Lease End:" /> <LocalDate date={leaseEnd} />
                </div>
              </div>
            )}

            {/* Button - Shows below text on small screens, inline on md+ */}
            {canOpenInjuryReport && (
              <div className="mt-3 md:hidden">
                <Button
                  className="m-0 w-full rounded-lg bg-gray-100 px-6 py-2.5 font-medium text-gray-700 transition-colors hover:bg-gray-200"
                  onClick={() => createReportMutation.mutate(id)}
                  disabled={hasSpecialStatus}
                >
                  <Translation id="Opprett skademelding" />
                </Button>
              </div>
            )}
          </div>
          {/* Button - Shows inline on md+ screens */}
          <div className="hidden flex-shrink-0 md:block">
            {canOpenInjuryReport && (
              <Button
                className="m-0 rounded-lg bg-gray-100 px-6 py-2.5 font-medium text-gray-700 transition-colors hover:bg-gray-200"
                onClick={() => createReportMutation.mutate(id)}
                disabled={hasSpecialStatus}
              >
                <Translation id="Opprett skademelding" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {buyoutOffering && <BuyOutOffering data={buyoutOffering} />}

      {/* Injury Reports Section */}
      {injuryReports?.length > 0 && !isBoughtOut && !isForReturn && (
        <InjuryReportList injuryReports={injuryReports} />
      )}
    </div>
  )
}
